import * as React from "react";

const MOBILE_BREAKPOINT = 768;
const PHONE_BREAKPOINT = 768;
const TABLET_BREAKPOINT = 1200;

export type FormFactor = 'phone' | 'tablet' | 'desktop';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * WHY THIS READS MEDIA QUERIES AND NOT `window.innerWidth`
 * ═══════════════════════════════════════════════════════════════════════════
 * `window.innerWidth` is not the viewport width once a page overflows
 * horizontally on a mobile browser — it grows to the content width. That made
 * this hook self-defeating, and it took the mobile navigation down completely:
 *
 *   1. the state initialises to 'desktop', so the FIRST render is the desktop
 *      TopBar, whose nav row is ~875px wide;
 *   2. on a 393px phone that widens the document to ~1432px;
 *   3. the effect then reads `window.innerWidth` — now 1432 — concludes
 *      "desktop", and renders the desktop nav again;
 *   4. the loop is stable, so the hamburger never appears and every route that
 *      is only reachable through the mobile menu is unreachable on a phone.
 *
 * Measured on the real stack at 393x851: `innerWidth` 1432, page overflow
 * 1039px, no hamburger. At 768x1024: `innerWidth` 1510, overflow 742px.
 * `matchMedia`, `documentElement.clientWidth` and `visualViewport.width` all
 * reported the true 393 / 768 throughout — only `innerWidth` was contaminated,
 * and it was the one signal being used.
 *
 * Media queries are also what Tailwind's `md:` / `lg:` variants resolve
 * against, so reading them here keeps the JS branch and the CSS branch
 * agreeing by construction rather than by coincidence.
 *
 * SSR-safe: `matchMedia` is absent during server render, so the initial state
 * is computed defensively and corrected on mount.
 */
const PHONE_QUERY = `(max-width: ${PHONE_BREAKPOINT - 1}px)`;
const TABLET_QUERY = `(min-width: ${PHONE_BREAKPOINT}px) and (max-width: ${TABLET_BREAKPOINT}px)`;

function readFormFactor(): FormFactor {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return 'desktop';
  }
  if (window.matchMedia(PHONE_QUERY).matches) return 'phone';
  if (window.matchMedia(TABLET_QUERY).matches) return 'tablet';
  return 'desktop';
}

export function useFormFactor(): FormFactor {
  // Seeded from the real query rather than defaulting to 'desktop', so the
  // first paint on a phone is already the phone layout and step 1 of the loop
  // above cannot start.
  const [formFactor, setFormFactor] = React.useState<FormFactor>(readFormFactor);

  React.useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;

    const update = () => setFormFactor(readFormFactor());
    update();

    // Listen to the queries themselves. A `resize` listener would also fire on
    // the content-driven width changes that caused the original bug.
    const phone = window.matchMedia(PHONE_QUERY);
    const tablet = window.matchMedia(TABLET_QUERY);
    phone.addEventListener('change', update);
    tablet.addEventListener('change', update);
    return () => {
      phone.removeEventListener('change', update);
      tablet.removeEventListener('change', update);
    };
  }, []);

  return formFactor;
}

// Convenience hooks
export function useIsPhone() {
  return useFormFactor() === 'phone';
}

export function useIsTablet() {
  return useFormFactor() === 'tablet';
}

export function useIsDesktop() {
  return useFormFactor() === 'desktop';
}

// Keep existing useIsMobile for backwards compatibility
export function useIsMobile() {
  const [isMobile, setIsMobile] = React.useState<boolean | undefined>(undefined);

  React.useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`);
    // Read `mql.matches`, not `window.innerWidth`. This hook already built the
    // right instrument and then ignored it — and innerWidth is the value that
    // inflates with content overflow, which is what broke useFormFactor.
    const onChange = () => setIsMobile(mql.matches);
    mql.addEventListener("change", onChange);
    setIsMobile(mql.matches);
    return () => mql.removeEventListener("change", onChange);
  }, []);

  return !!isMobile;
}
